import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { LOGOS_BUCKET } from '@/lib/storage';
import type { SystemHealth } from '@/types';

export interface SystemInfo {
  projects: { total: number; active: number; progress: number; inactive: number };
  billing: { receipts: number; total: number };
  finance: { income: number; expense: number; balance: number };
}

export interface HealthItem {
  key: string;
  label: string;
  icon: string;
  status: SystemHealth;
  detail: string;
}

export interface SystemSnapshot {
  info: SystemInfo | null;
  health: HealthItem[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const EMPTY_INFO: SystemInfo = {
  projects: { total: 0, active: 0, progress: 0, inactive: 0 },
  billing: { receipts: 0, total: 0 },
  finance: { income: 0, expense: 0, balance: 0 },
};

export function useSystemInfo(): SystemSnapshot {
  const [info, setInfo] = useState<SystemInfo | null>(null);
  const [health, setHealth] = useState<HealthItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const nextInfo: SystemInfo = { ...EMPTY_INFO };
    const healthItems: HealthItem[] = [];

    // Base de datos
    try {
      const [projectsRes, receiptsRes, movementsRes] = await Promise.all([
        supabase.from('projects').select('status'),
        supabase.from('receipts').select('amount'),
        supabase.from('financial_movements').select('movement_type, amount'),
      ]);
      if (projectsRes.error || receiptsRes.error || movementsRes.error) throw projectsRes.error;

      const projects = projectsRes.data ?? [];
      nextInfo.projects = {
        total: projects.length,
        active: projects.filter((p) => p.status === 'ACTIVO').length,
        progress: projects.filter((p) => p.status === 'EN PROCESO').length,
        inactive: projects.filter((p) => p.status === 'DESACTIVADO').length,
      };

      const receipts = receiptsRes.data ?? [];
      nextInfo.billing = {
        receipts: receipts.length,
        total: receipts.reduce((sum, r) => sum + Number(r.amount ?? 0), 0),
      };

      const movements = movementsRes.data ?? [];
      const income = movements
        .filter((m) => m.movement_type === 'INGRESO')
        .reduce((sum, m) => sum + Number(m.amount ?? 0), 0);
      const expense = movements
        .filter((m) => m.movement_type === 'EGRESO')
        .reduce((sum, m) => sum + Number(m.amount ?? 0), 0);
      nextInfo.finance = { income, expense, balance: income - expense };

      setInfo(nextInfo);
      healthItems.push({
        key: 'database',
        label: 'Base de datos',
        icon: 'ri-database-2-line',
        status: 'operativo',
        detail: 'Conexión establecida correctamente.',
      });
    } catch {
      setInfo(EMPTY_INFO);
      setError('No se pudo obtener la información del sistema.');
      healthItems.push({
        key: 'database',
        label: 'Base de datos',
        icon: 'ri-database-2-line',
        status: 'error',
        detail: 'No fue posible conectar con la base de datos.',
      });
    }

    // Autenticación
    try {
      const { error: authError } = await supabase.auth.getSession();
      healthItems.push({
        key: 'auth',
        label: 'Autenticación',
        icon: 'ri-shield-user-line',
        status: authError ? 'advertencia' : 'operativo',
        detail: authError ? 'No se pudo verificar la sesión.' : 'Servicio de autenticación activo.',
      });
    } catch {
      healthItems.push({
        key: 'auth',
        label: 'Autenticación',
        icon: 'ri-shield-user-line',
        status: 'error',
        detail: 'El servicio de autenticación no responde.',
      });
    }

    // Almacenamiento
    try {
      const { error: storageError } = await supabase.storage
        .from(LOGOS_BUCKET)
        .list('', { limit: 1 });
      healthItems.push({
        key: 'storage',
        label: 'Almacenamiento',
        icon: 'ri-cloud-line',
        status: storageError ? 'advertencia' : 'operativo',
        detail: storageError
          ? 'El almacenamiento respondió con advertencias.'
          : 'Almacenamiento de archivos disponible.',
      });
    } catch {
      healthItems.push({
        key: 'storage',
        label: 'Almacenamiento',
        icon: 'ri-cloud-line',
        status: 'error',
        detail: 'No fue posible acceder al almacenamiento.',
      });
    }

    // Estado global del sistema
    const globalStatus: SystemHealth = healthItems.some((item) => item.status === 'error')
      ? 'error'
      : healthItems.some((item) => item.status === 'advertencia')
        ? 'advertencia'
        : 'operativo';
    healthItems.push({
      key: 'system',
      label: 'Sistema',
      icon: 'ri-server-line',
      status: globalStatus,
      detail:
        globalStatus === 'operativo'
          ? 'Todos los servicios funcionan correctamente.'
          : globalStatus === 'advertencia'
            ? 'El sistema opera con algunas advertencias.'
            : 'El sistema presenta problemas de conexión.',
    });

    setHealth(healthItems);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { info, health, loading, error, reload: load };
}